/**
 * This device's entry in users/{uid}.pushDevices (2026-10-22). The server
 * sends a rider's alerts to every device listed there (firebase/functions/
 * riderPushTokens.js), so the website or a second phone no longer takes over
 * the one address, and a logout removes only this device. Pure, no Firebase.
 */
export const PUSH_DEVICE_KEY = 'mt_push_device_id';
export const MAX_PUSH_DEVICES = 10;
export const PUSH_DEVICE_STALE_MS = 60 * 24 * 60 * 60 * 1000;
const ID_RE = /^[A-Za-z0-9_]{6,64}$/;

const randomHex = (bytes) => {
  try {
    const a = new Uint8Array(bytes);
    (globalThis.crypto || window.crypto).getRandomValues(a);
    return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return Array.from({ length: bytes * 2 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  }
};

/** Stable per install (app) or per browser (website). Letters and digits only: safe as a field name. */
export const getPushDeviceId = (storage = (typeof localStorage !== 'undefined' ? localStorage : null)) => {
  try {
    const saved = storage?.getItem(PUSH_DEVICE_KEY);
    if (saved && ID_RE.test(saved)) return saved;
    const id = `d${randomHex(12)}`;
    storage?.setItem(PUSH_DEVICE_KEY, id);
    return id;
  } catch {
    return null; // no storage: fall back to the single fcmToken only
  }
};

const toMs = (v) => {
  if (!v) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  return null;
};

/**
 * The device map to save: this device's entry replaced, other entries with the
 * same token or not seen for 60 days dropped, and at most MAX_PUSH_DEVICES
 * kept (newest first; this device always stays).
 */
export const nextPushDevices = (existing, deviceId, entry, now = Date.now()) => {
  const others = Object.entries(existing && typeof existing === 'object' ? existing : {})
    .filter(([id, e]) => id !== deviceId && ID_RE.test(id) && e && typeof e.token === 'string')
    .filter(([, e]) => e.token !== entry.token)
    .filter(([, e]) => {
      const ms = toMs(e.updatedAt);
      return ms == null || now - ms <= PUSH_DEVICE_STALE_MS;
    })
    .sort(([, a], [, b]) => (toMs(b.updatedAt) || now) - (toMs(a.updatedAt) || now))
    .slice(0, MAX_PUSH_DEVICES - 1);
  return { ...Object.fromEntries(others), [deviceId]: entry };
};

/** The device map without this device (on logout). */
export const withoutPushDevice = (existing, deviceId) => {
  const out = { ...(existing && typeof existing === 'object' ? existing : {}) };
  delete out[deviceId];
  return out;
};
